import React from 'react';
import {
  useActiveGuide,
  useFetchGuideById,
  useFetchGuideSizeLimit,
  useFetchPageById,
} from '../../queries/guides';
import { useGuideStore } from '../../stores/guideStore';
import { useThemesetByGuideId } from '../../queries/themesets';
import { useTheme } from 'styled-components';
import { defaultBynderThemeset } from '../../constants/themesets';
import { useHistory } from 'react-router-dom';
import { ROUTES, isDevelopment } from '../../constants/app';
import { useTranslate } from '@bynder/localization';
import { MAX_GUIDE_PERCENTAGE_BEFORE_NOTIFICATION } from '../../constants/guides';
import { Thumbnail, notify } from '@bynder/design-system';
import { IconWarning } from '@bynder/icons';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { size } from '../../constants/styles';
import { EntityVisibility, GuideType } from '../../types';
import { GAtrackNavigation } from '../../helpers/googleAnalytics';
import { useSendView } from '../../mutations/analytics';
import { useInspectorStore } from '../../stores/inspectorStore';
import { HomepagePermissions, useHasAnyPermission, UserType, useUserType } from '../../helpers/hooks';
import { JWT_PERMISSIONS } from '../../helpers/permissions';

export const useSetActiveChapter = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const setActiveChapterId = useGuideStore(s => s.setActiveChapterId);

  const { data: guide } = useFetchGuideById(activeGuideId);

  React.useEffect(() => {
    if (guide) {
      const activeChapter = guide?.chapters
        .flat()
        .find(chapter => chapter.pages.find(page => page.id === activePageId));
      setActiveChapterId(activeChapter?.id);
    }
  }, [activeGuideId, activePageId, guide, setActiveChapterId]);
};

export const useGuideThemeset = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { data: themeset } = useThemesetByGuideId(activeGuideId);

  const { setAssignedThemeset } = useTheme();

  React.useEffect(() => {
    if (themeset) {
      if (!isEditMode) {
        setAssignedThemeset(themeset);
      } else {
        setAssignedThemeset({
          ...defaultBynderThemeset,
          colorSets: themeset?.colorSets,
          typographies: themeset?.typographies,
        });
      }
    }
  }, [themeset, setAssignedThemeset, isEditMode]);
};

export const useRedirectToFirstViewablePage = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const history = useHistory();
  const { data: guide, isError } = useFetchGuideById(activeGuideId);

  const firstViewablePageId =
    !isError &&
    guide?.chapters.flatMap(chapter => chapter.pages).find(page => page.canView)
      ?.id;

  const firstEditablePageId =
    !isError &&
    guide?.chapters.flatMap(chapter => chapter.pages).find(page => page.canEdit)
      ?.id;

  React.useEffect(() => {
    if (!activePageId && (firstEditablePageId || firstViewablePageId)) {
      if (isEditMode) {
        history.replace({
          pathname: ROUTES.GUIDE_PAGE_EDIT(activeGuideId, firstEditablePageId),
        });
      } else {
        history.replace({
          pathname: firstViewablePageId
            ? ROUTES.GUIDE_PAGE(activeGuideId, firstViewablePageId)
            : ROUTES.GUIDE_PAGE(activeGuideId, firstEditablePageId),
        });
      }
    }
  }, [
    firstEditablePageId,
    firstViewablePageId,
    activeGuideId,
    isEditMode,
    history,
  ]);

  return { firstEditablePageId, firstViewablePageId };
};

export const useManagerNavigation = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const previousIsEditMode = React.useRef(isEditMode);
  const history = useHistory();
  const { data: guide, isError, isLoading } = useFetchGuideById(activeGuideId);
  const isManager = useHasAnyPermission([
    HomepagePermissions.MANAGE,
    JWT_PERMISSIONS.GUIDELINES_MANAGE,
  ]);

  React.useEffect(() => {
    if (!guide || isError || isLoading || !isManager) return;

    const lastPageId = guide.chapters[0]?.pages?.slice(-1)[0]?.id;
    const switchedToEditMode = previousIsEditMode.current === false && isEditMode;
    
    // Manager navigating from view to edit mode with targeted pages enabled
    if (
      switchedToEditMode && 
      guide.targetedPagesEnabled && 
      lastPageId &&
      activePageId !== lastPageId
    ) {
      history.replace({
        pathname: ROUTES.GUIDE_PAGE_EDIT(activeGuideId, lastPageId),
      });
    }

    // Update the previous edit mode ref for the next render
    previousIsEditMode.current = isEditMode;
  }, [activeGuideId, activePageId, guide, history, isEditMode, isError, isLoading, isManager]);
};

export const useRedirectToDefaultPage = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const history = useHistory();
  const { data: guide, isError, isLoading } = useFetchGuideById(activeGuideId);

  React.useEffect(() => {
    if (!guide || isError || isLoading) return;

    const isHomepage = guide.type === GuideType.HOME_PAGE;
    const lastPageId = guide.chapters[0]?.pages?.slice(-1)[0]?.id;
    const shouldRedirect =
      !guide.targetedPagesEnabled &&
      isEditMode &&
      isHomepage &&
      activePageId !== lastPageId;

    if (shouldRedirect) {
      history.replace({
        pathname: ROUTES.GUIDE_PAGE_EDIT(activeGuideId, lastPageId),
      });
    }
  }, [activeGuideId, activePageId, guide, history, isEditMode, isError, isLoading]);
};

export const useGuideSizeLimit = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const { data } = useFetchGuideSizeLimit(activeGuideId);
  const { translate } = useTranslate();
  const notificationId = 'size_limit_id';

  React.useEffect(() => {
    if (
      Number(data?.spaceUsedPercentage) >=
        MAX_GUIDE_PERCENTAGE_BEFORE_NOTIFICATION &&
      isEditMode
    ) {
      notify({
        thumbnail: (
          <Thumbnail
            variant="clean"
            shape="circle"
            backgroundColor="gray50a"
            icon={<IconWarning />}
            iconColor="#334E5C"
          />
        ),
        isPersistent: true,
        id: notificationId,
        title: translate('NOTIFICATION.GUIDE_LIMIT_HEADER'),
        description: translate('NOTIFICATION.GUIDE_LIMIT_CONTENT', {
          size: Math.round(Number(data.spaceUsedPercentage)),
        }),
      });
    }
  }, [data?.spaceUsedPercentage, isEditMode, translate]);
};

export const useOnlyAllowEditingInLaptops = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const isEditMode = useGuideStore(s => s.isEditMode);
  const setIsEditMode = useGuideStore(s => s.setIsEditMode);
  const { width } = useWindowDimensions();

  React.useEffect(() => {
    if (width < size.laptop && isEditMode) {
      setIsEditMode(false);
    }
  }, [width, isEditMode, setIsEditMode, activeGuideId]);
};

export const useSetGuideVersion = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const setActiveGuideVersion = useGuideStore(s => s.setActiveGuideVersion);

  const { data: guide } = useFetchGuideById(activeGuideId);

  React.useEffect(() => {
    if (guide) {
      setActiveGuideVersion(guide?.version);
    }
  }, [guide]);

  return null;
};

export const useHeaderVisibility = () => {
  const { userType } = useUserType();
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const { data: guide } = useFetchGuideById(activeGuideId);
  const { data: themeset } = useThemesetByGuideId(activeGuideId);
  const isGuidePublic = guide?.visibility === EntityVisibility.PUBLIC;
  const isPublicUser = userType === UserType.PUBLIC;
  const isHeaderHidden = !themeset?.properties?.pageHeaderEnabled;
  const mainHeader = document.getElementById('mainHeader');

  React.useEffect(() => {
    const observer = new MutationObserver((_mutationsList, observer) => {
      if (isHeaderHidden && mainHeader && isGuidePublic && isPublicUser) {
        mainHeader.style.display = 'none';
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      const mainHeader = document.getElementById('mainHeader');
      if (mainHeader) mainHeader.style.display = 'block';
    };
  }, [guide?.visibility, themeset?.properties?.pageHeaderEnabled]);

  return null;
};

export const useGoogleAnalyticsTrackNavigation = () => {
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const { data: guide, isLoading } = useFetchGuideById(activeGuideId);

  React.useEffect(() => {
    if (!isLoading && !isDevelopment) {
      GAtrackNavigation(guide?.title);
    }
  }, [isLoading, guide?.title]);

  return null;
};

export const useTrackGuideNavigation = () => {
  const sendView = useSendView();
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const { isLoading } = useFetchGuideById(activeGuideId);
  const { data: guide } = useActiveGuide();

  React.useEffect(() => {
    if (!isLoading) {
      sendView.mutate({
        name: 'GuideOpened',
        guideType: guide?.type,
      });
    }
  }, [isLoading, activeGuideId]);

  return null;
};

export const useTrackPageNavigation = () => {
  const sendView = useSendView();
  const activeGuideId = useGuideStore(s => s.activeGuideId);
  const activePageId = useGuideStore(s => s.activePageId);
  const { data: page, isLoading } = useFetchPageById(
    activeGuideId,
    activePageId,
  );
  const { data: guide } = useActiveGuide();

  React.useEffect(() => {
    if (!isLoading) {
      sendView.mutate({
        name: 'PageOpened',
        guideType: guide?.type,
      });
    }
  }, [isLoading, page?.title]);

  return null;
};

export const useClearStoreOnUnmountGuide = () => {
  const setActivePageId = useGuideStore(s => s.setActivePageId);
  const setActiveGuideId = useGuideStore(s => s.setActiveGuideId);
  const setIsEditMode = useGuideStore(s => s.setIsEditMode);
  const setIsInspectorOpen = useInspectorStore(s => s.setIsInspectorOpen);

  React.useEffect(() => {
    return () => {
      setActiveGuideId(null);
      setActivePageId(null);
      setIsEditMode(false);
      setIsInspectorOpen(false);
    };
  }, []);

  return null;
};

export const useResetScroll = guideContentRef => {
  const activePageId = useGuideStore(s => s.activePageId);
  const history = useHistory();
  React.useEffect(() => {
    const hash = history.location.hash;

    if (guideContentRef.current && !hash) {
      guideContentRef.current.scrollTo(0, 0);
    }
  }, [activePageId]);

  return null;
};
